import { template } from "@ember/template-compiler";
import { guidFor } from '@ember/object/internals';
import type BioExamResult from 'parog-web/models/bio-exam-result';
import type { TOC } from '@ember/component/template-only';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { IntlService } from 'ember-intl';
import ValueWithUnit from 'parog-web/components/value-with-unit';
import { Popover } from 'ember-primitives';
import type JournalEventModel from 'parog-web/models/journal-event';
import type BioExamResultsReceivedJE from 'parog-web/models/bio-exam-results-received-journal-event';
import { alertingStyleClassName, alertingStyleClassNameBlack } from 'parog-web/modifiers/alerting-je-style';
export interface S {
    Args: {
        originJournalEvent?: JournalEventModel;
        cellResult: CellBioExamResult;
        /**
     * The id of the show references tooltip hook element,
     * provided by the parent component tracking the mouseover events
     * */ activeReferencesHook?: string;
    };
}
export const referencesHookClass = 'cell-references-hook';
/**
 * Wrapping class around a possible BioExamResult to encapsulate
 * the alerting style associated with the BioExamResultsReceivedJE
 * */ export class CellBioExamResult {
    constructor(public result: BioExamResult | undefined, public originJournalEvent: BioExamResultsReceivedJE){}
    /** Styles to represent if there is an alert on the BioExamResultsReceivedJE for the LOINC code of this BioExamResult */ get styles() {
        const isAlerting1 = this.result ? this.originJournalEvent.hasAlertForCode(this.result.code) : false;
        return isAlerting1 ? `${alertingStyleClassName} ${alertingStyleClassNameBlack}` : '';
    }
}
const ReferencesHook: TOC<{
    Element: HTMLElement;
}> = template(`
  <i
    ...attributes
    class='{{referencesHookClass}}
      circle info icon hover:text-grey ml-[calc(0.5em_+_4px)] inline text-[#80808066]'
  ></i>
`, {
    eval () {
        return eval(arguments[0]);
    }
});
export default class BioExamResultsSummaryRowCellComponent extends Component<S> {
    @service
    intl: IntlService;
    referencesHookClass = referencesHookClass;
    referencesHookId = guidFor(this);
    referencesHookSelector = `#${this.referencesHookId}`;
    result: CellBioExamResult['result'];
    constructor(owner1, args1: S['Args']){
        super(owner1, args1);
        this.result = args1.cellResult.result;
    }
    /** Should we display the references tooltip for this cell */ get showReferences() {
        return (this.args.activeReferencesHook && this.args.activeReferencesHook === this.referencesHookId);
    }
    get minValue() {
        if (!this.result) return;
        return this.result.resultHash.normValInf;
    }
    get maxValue() {
        if (!this.result) return;
        return this.result.resultHash.normValSupp;
    }
    get shouldDisplayReferences() {
        return this.minValue || this.maxValue;
    }
    get displayedValue() {
        if (!this.result) return;
        const displayedValue1 = this.result.displayedValue;
        if ([
            'positive',
            'negative'
        ].includes(displayedValue1)) return this.intl.t('bioExamResultCodes.value', {
            value: displayedValue1
        });
        return displayedValue1;
    }
    get tooltipText() {
        return `Références : [${this.minValue ?? ' -'} : ${this.maxValue ?? '- '}]`;
    }
    get unitStyles() {
        return this.args.cellResult.styles || 'text-[#808080]';
    }
    static{
        template(`
    <td class='center aligned'>
      {{#if this.result.value}}
        {{#if this.result.unit}}
          <ValueWithUnit
            @value={{this.displayedValue}}
            @valueStyles={{@cellResult.styles}}
            @unit={{this.result.unit}}
            @unitStyles={{this.unitStyles}}
          />
        {{else}}
          {{#if @cellResult.styles}}
            <span class={{@cellResult.styles}}>{{this.displayedValue}}</span>
          {{else}}
            {{this.displayedValue}}
          {{/if}}
        {{/if}}

        {{#if this.shouldDisplayReferences}}
          {{#if this.showReferences}}
            <Popover
              @inline={{true}}
              @placement='top'
              @offsetOptions={{6}}
              as |p|
            >
              <ReferencesHook {{p.reference}} id={{this.referencesHookId}} />
              <p.Content
                class='border-1 border-grey-medium absolute left-0 top-0 w-max rounded bg-white p-4 shadow'
              >
                {{this.tooltipText}}
                <div
                  class='border-b-grey-medium border-r-grey-medium absolute h-2 w-2 rotate-45 border border-l-0 border-t-0 bg-white'
                  {{p.arrow}}
                ></div>
              </p.Content>
            </Popover>
          {{else}}
            <ReferencesHook id={{this.referencesHookId}} />
          {{/if}}
        {{/if}}
      {{else}}
        —
      {{/if}}
    </td>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
